import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

// Popover — radix wrapper for anchored, dismissible panels. Use for
// per-section settings, inline editors, and contextual menus that
// hold custom content (anything more complex than a list of menu
// items, which is what DropdownMenu is for).

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  // container forwards to Radix Portal. Set this to a native <dialog>
  // element when the Popover is rendered inside one — `dialog.showModal()`
  // puts the dialog in the browser's top-layer, and anything portaled to
  // document.body (Radix's default) sits BELOW it, so the panel would
  // appear hidden behind the dialog. Portaling into the dialog itself makes
  // the panel share the same top-layer stacking context. Mirrors SelectContent.
  container?: HTMLElement | null;
};

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'end', sideOffset = 6, container, ...props }, ref) => (
  <PopoverPrimitive.Portal container={container ?? undefined}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-2xl border border-border bg-background p-4 shadow-lg outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
